import { FC, Fragment, useCallback, useEffect, useState } from "react"

import { ReactComponent as IconSearch } from "@common_assets/svg/search.svg"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  Autocomplete,
  Box,
  Checkbox,
  debounce,
  InputAdornment,
  Stack,
  TextField as MuiTextField,
} from "@mui/material"
import { useMutationCreateSchoolGroup } from "api/reactQuery/mutations/schoolGroups"
import { useInfiniteQuerySchoolGroupNewCandidates } from "api/reactQuery/queries/schoolGroups"
import Dialog from "components/common/dialog/dialog"
import IconWrapper from "components/common/icon/iconWrapper"
import TextField from "components/form/common/field/textField"
import { useCustomSnackbar } from "hooks/snackbar"
import { getGroupMutationFeedback } from "pages/groups/components/getGroupMutationFeedback"
import { renderGroupMemberTags } from "pages/groups/components/renderGroupMemberTags"
import { useGroupMembersListboxMaxHeight } from "pages/groups/components/useGroupMembersListboxMaxHeight"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { IGroup } from "ts/interfaces/Group"
import { IObjectStudentDetailed } from "ts/interfaces/Student"
import { requiredStringSchema } from "utils/yup"
import * as yup from "yup"

// Structurally satisfied by the /groups/candidates response so the same Autocomplete
// can mix pre-selected members with search results without converting either shape.
type IMemberOption = Pick<IObjectStudentDetailed, "id" | "first_name" | "last_name">

interface IFormValues {
  name: string
  members: IMemberOption[]
}

const defaultValues: IFormValues = { name: "", members: [] }

const schema = yup.object({
  name: requiredStringSchema,
  members: yup.array().default([]),
})

interface IProps {
  open: boolean
  onClose: () => void
  onCreated?: (group: IGroup) => void
}

const CreateSchoolGroupDialog: FC<IProps> = ({ open, onClose, onCreated }) => {
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const [search, setSearch] = useState("")
  const [isMembersFocused, setIsMembersFocused] = useState(false)
  const { listboxMaxHeight, onOpen: onListboxOpen, onClose: onListboxClose } =
    useGroupMembersListboxMaxHeight()

  const methods = useForm<IFormValues>({
    resolver: yupResolver(schema),
    defaultValues,
  })

  // Purpose-built endpoint for this exact case — candidates for a group that doesn't
  // exist yet, school-wide, paginated (unlike /students/search).
  const {
    data,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuerySchoolGroupNewCandidates({
    filter: search,
    options: { enabled: open, keepPreviousData: true },
  })
  const options =
    data?.pages.flatMap((page) =>
      page.data.objects.map((member) => ({
        id: member.id,
        first_name: member.first_name ?? "",
        last_name: member.last_name ?? "",
      }))
    ) ?? []

  const { ref: loadMoreRef, inView: isLoadMoreInView } = useInView()

  // Deps deliberately narrowed to isLoadMoreInView alone (see EditSchoolGroupDialog /
  // Employees.tsx) — including hasNextPage/isFetchingNextPage/fetchNextPage re-fires
  // this on the still-stale inView value left over from the just-unmounted sentinel.
  useEffect(() => {
    if (isLoadMoreInView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [isLoadMoreInView])

  const debouncedSetSearch = useCallback(debounce(setSearch, 300), [])

  const { mutate, isLoading } = useMutationCreateSchoolGroup({
    options: {
      onSuccess: (response) => {
        showSnackbar({ title: t("group.groupCreatedSuccess") })
        onCreated?.(response.data)
        handleClose()
      },
      onError: (error) => {
        const { onNameField, message, isRaw, nameError } =
          getGroupMutationFeedback(error)
        if (onNameField && nameError) {
          methods.setError("name", nameError)
          return
        }
        showSnackbar({
          title: isRaw ? message : t(message),
          variant: "error",
        })
      },
    },
  })

  const handleClose = () => {
    methods.reset(defaultValues)
    setSearch("")
    setIsMembersFocused(false)
    onClose()
  }

  const onSubmit = methods.handleSubmit((values) => {
    if (isLoading) return
    mutate({
      data: {
        name: values.name,
        student_ids: values.members.map((member) => member.id),
      },
    })
  })

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      titleText={t("group.createNewGroup")}
      sx={(theme) => ({
        "& .MuiDialog-paper": {
          maxWidth: "870px",
          borderRadius: "8px",
          border: `1px solid ${theme.palette.mockup.neutral90}`,
          backgroundColor: theme.palette.mockup.neutral100,
        },
      })}
      actionAcceptText={t("group.createGroupCta")}
      isLoading={isLoading}
      actionAcceptButtonProps={{ disabled: isLoading }}
      onActionButtonClick={onSubmit}
    >
      <Box>
        <FormProvider {...methods}>
          <Stack gap="24px">
            <TextField
              name="name"
              label={t("group.groupNameLabel")}
              placeholder={t("group.groupNameLabel")}
              fullWidth
            />
            <Controller
              name="members"
              control={methods.control}
              render={({ field: { value, onChange } }) => (
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  loading={isFetching}
                  options={options}
                  value={value}
                  ListboxProps={{
                    sx: { maxHeight: listboxMaxHeight },
                  }}
                  onOpen={onListboxOpen}
                  onClose={onListboxClose}
                  onFocus={() => setIsMembersFocused(true)}
                  onBlur={() => setIsMembersFocused(false)}
                  onChange={(_, newValue) => onChange(newValue)}
                  onInputChange={(_, newInputValue) =>
                    debouncedSetSearch(newInputValue)
                  }
                  isOptionEqualToValue={(option, val) => option.id === val.id}
                  getOptionLabel={(option) =>
                    `${option.first_name} ${option.last_name}`
                  }
                  renderOption={(props, option, { selected }) => {
                    const { key: _key, ...optionProps } = props
                    const isLastOption =
                      option.id === options[options.length - 1]?.id
                    return (
                      <Fragment key={option.id}>
                        <Box component="li" {...optionProps}>
                          <Checkbox checked={selected} sx={{ mr: "8px" }} />
                          {option.first_name} {option.last_name}
                        </Box>
                        {isLastOption && hasNextPage && (
                          <Box ref={loadMoreRef} />
                        )}
                      </Fragment>
                    )
                  }}
                  renderTags={(members, getTagProps) =>
                    renderGroupMemberTags(
                      isMembersFocused,
                      members,
                      getTagProps
                    )
                  }
                  renderInput={(params) => (
                    <MuiTextField
                      {...params}
                      label={t("group.searchStudentLabel")}
                      placeholder={
                        value.length === 0
                          ? t("group.searchStudentLabel")
                          : undefined
                      }
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <IconWrapper>
                                <IconSearch />
                              </IconWrapper>
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          </Stack>
        </FormProvider>
      </Box>
    </Dialog>
  )
}

export default CreateSchoolGroupDialog
